'use client';

import { type ReactNode } from 'react';
import { useStageInsetHost } from './useStageInsets';

/**
 * The right column, floating over the map's right edge instead of taking it.
 *
 * In flow the column was a flex sibling of the map, so opening it handed
 * 288 to 560 px of width to a panel AND fired `map.resize()`
 * (`DroneMap.tsx:781`) - which re-projects every symbol on screen. That is the
 * first click of the golden flow: selecting a dock or an aircraft moved every
 * other aircraft the operator was watching, at the exact moment they had picked
 * one out. Floating it is what ends that.
 *
 * It is a card and not a column. Floating it out of the flex row ended the
 * resize, but `inset-y-0` left it covering the same full-height rectangle it had
 * reserved, so it still read as the bar it replaced. It is anchored to the top
 * right with a gap on both edges and no bottom, capped at the stage height less
 * those gaps, so its height is the height of the sheet inside it: a two-bay dock
 * is a small window with live map under it, and a full aircraft read grows to
 * the cap and scrolls within.
 *
 * This host is a position, not a policy. The tab strip and the four sheets are
 * unchanged and it knows nothing about which of them is showing. What the height
 * change does ask of them is that they stop stretching: each one carried
 * `lg:h-full`, which fills whatever it is given and would have made the card
 * full height whatever this element says. They are `lg:h-auto` now, and the
 * scroll that used to happen because every height was definite happens instead
 * because a flex column against its `max-height` shrinks the children that
 * declare `min-h-0`.
 *
 * What it MUST do is declare its width. Everything anchored to the map's right
 * edge - the map control column, the zones panel, the manual layout's corner
 * picture, and the centre the command bar is drawn around - has only ever been
 * safe because the map narrowed when this column opened. Now that
 * it does not, `CopStageInsets.right` is the only thing keeping those surfaces
 * out from under the panel, so the register and the float land together (D9).
 */

export interface CopSidePanelProps {
  /**
   * Whether the column is currently over the map, for the REGISTRY only.
   *
   * Deliberately not a class switch. Whether it floats is decided in CSS by the
   * `lg:` variants below, so crossing the breakpoint restyles ONE DOM tree
   * rather than re-parenting the sheets into a different one - the trap
   * `CopBottomStack` records, where swapping the element at a position unmounts
   * everything under it and silently resets what those components hold.
   *
   * Below `lg` each sheet is its own `fixed` drawer, so the column covers no map
   * at all there; reporting a width would shrink the measured control surface
   * for nothing and refuse manual flight that is available.
   */
  floating: boolean;
  /**
   * What is in the panel right now, for the landmark's accessible name.
   *
   * Only `DroneWorkspace` brings its own `<aside aria-label>`; the dock sheet,
   * the missions queue and the two route editors are plain divs. Without a name
   * here a screen-reader operator gets no landmark to jump to and nothing says a
   * region has opened over the map. On the host so that a sheet added later
   * cannot forget it, which is the same argument as the opaque ground below.
   */
  label: string;
  children: ReactNode;
}

export function CopSidePanel({ floating, label, children }: CopSidePanelProps) {
  const ref = useStageInsetHost<HTMLDivElement>('right', floating);

  return (
    <div
      ref={ref}
      role="region"
      aria-label={label}
      data-testid="cop-side-panel"
      data-floating={floating ? 'true' : 'false'}
      // `contents` below `lg`: no box, so the sheets are exactly the children of
      // the row they have always been and each one's own fixed-bottom drawer
      // behaves as it does today.
      //
      // At `lg` this is a CARD, not a column. `top-3 right-3`, `bottom-auto`,
      // and a cap of the stage height less both gaps - so the panel is as tall
      // as the sheet inside it needs and no taller. A dock with two bays is a
      // short window with live map under it; a full aircraft read grows to the
      // cap and scrolls inside. `inset-y-0` is what kept this reading as the bar
      // it replaced: it stopped taking the map's width and then covered the
      // exact same rectangle.
      //
      // The WIDTH is untouched, and so is what this publishes.
      // `useStageInsetHost` measures `rect.width` on a right edge, so a 300 px
      // tall card still reserves the full right strip and nothing anchored to
      // the map's right edge moves when the operator switches tabs. The map
      // under a short card is uncovered but not reclaimed - that is a change to
      // CopStageInsets, not a class here.
      //
      // `overflow-clip` is what keeps the corners round: the tab strip and every
      // sheet head paint their own ground, and unclipped they square off the top
      // of the card. `clip` and never `hidden`, per the rule CopMissionCompose
      // records - `hidden` is a scroll container with no scrollbar that the
      // operator cannot scroll back. Clipping is only safe because the four
      // sheets that hung their resize handle 5 px OUTSIDE their left edge now
      // hang it inside, the way DroneWorkspace always did: a clipped box and an
      // outside handle cannot both be right, and the handle is the one that had
      // to move.
      //
      // The opaque ground, the border, the radius and the shadow are all on the
      // HOST. Three of the four sheets set `lg:bg-transparent` and the tab strip
      // is `bg-card/60`, which was right while this column sat over the page
      // background and is not right over a live map: at 40% show-through a
      // panning basemap and the aircraft symbols on it run behind bay batteries
      // and mission rows, and `DockSidebar` stacks its cards with `gap-2`, so
      // 8 px stripes of moving map would show between an aircraft's readings.
      // Putting it here rather than on each sheet means a sheet added later
      // cannot forget it - and it is why the sheets that used to draw their own
      // `lg:rounded-lg lg:border` no longer do. Two rounded rectangles 1 px
      // apart read as a rendering artifact, which is the same argument the old
      // no-left-border note made about the resize hairline.
      className="contents lg:absolute lg:right-3 lg:top-3 lg:bottom-auto lg:z-20 lg:flex lg:max-h-[calc(100%-1.5rem)] lg:w-[var(--dock-sheet-width)] lg:flex-col lg:overflow-clip lg:rounded-lg lg:border lg:border-subtle lg:bg-card lg:shadow-sheet"
    >
      {children}
    </div>
  );
}
